import config from "../config/config";
import { reactLocalStorage } from "reactjs-localstorage";
import moment from "moment-timezone";

const MiddlewareClient = class MiddlewareClient {
	/**
	 * Constructor.
	 */
	constructor(initAuthResponse = {}) {
		this.endpoint = config.middleware.url;
		this.lastAuthResponse = initAuthResponse;
	}

	/**
	 * Authenticate on Middleware.
	 *
	 * @param username Username.
	 * @param password Password.
	 * @returns {Promise}
	 */
	authenticate = async (username, password) => {
		return new Promise(async (resolve, reject) => {
			const response = await this.post("/authenticate/login", {
				username: username,
				password: password,
				kiosk: true
			});
			if (response.hasOwnProperty("error") && response.error) {
				resolve(false);
			} else {
				this.lastAuthResponse = response;
				if ( response ) {
					reactLocalStorage.setObject("authInfo", response);
				}
				resolve(response);
			}
		});
	};

	/**
	 * Gets data from last auth request.
	 *
	 * @returns {{}|*}
	 */
	getLastAuthData = () => {
		return this.lastAuthResponse;
	};

	/**
	 * Get resource schedule for next ${nextDays} days.
	 *
	 * @param nextDays Number of days.
	 * @return {Promise}
	 */
	pullAttendance = (nextDays = 1, spooftime) => {
		return new Promise(async (resolve, reject) => {
			let club = reactLocalStorage.get("club");
			let resource = reactLocalStorage.get("resource");
			let querystring = `days=${nextDays}&club=${club}&resource=${resource}`;

			if (spooftime) {
				spooftime = moment(spooftime).unix();
				querystring = `${querystring}&time=${spooftime}`;
			}

			const response = await this.get(`/attendance?${querystring}`);
			if (response.hasOwnProperty("error") && response.error) {
				resolve(false);
			} else {
				resolve(response.classes || false);
			}
		});
	};

	markAttended = (memberId, classId, classDate) => {
		return new Promise(async (resolve, reject) => {
			const response = await this.post(`/checkin`, {"memberId": memberId,"classId": classId,"classDate": classDate});
			if (response.hasOwnProperty("error") && response.error) {
				resolve(false);
			} else {
				resolve(response || false);
			}
		});
	}

	/**
	 * Access API via POST method.
	 *
	 * @param route API route.
	 * @param data Request body.
	 * @returns {Promise.<*>}
	 */
	post = async (route, data = {}) => {
		try {
			this.lazyLoadAuthInfo();
			const authToken = reactLocalStorage.get("authToken", "");
			const response = await fetch(this.endpoint + route, {
				method: "post",
				mode: "cors",
				headers: {
					Accept: "application/json",
					"Content-Type": "application/json",
					"X-App-Session-Id": this.lastAuthResponse.sessionId || "",
					"X-App-Token": authToken
				},
				body: JSON.stringify(data)
			});

			if (response.ok) {
				this.extractNewSessionId(response);
				this.extractNewAuthToken(response);
				let jsonResponse = await response.json();
				return jsonResponse;
			} else {
				this.lastPostResponse = response;
				reactLocalStorage.clear();
				window.location.reload();
				return false;
			}
		} catch ($exception) {
			return false;
		}
	};

	/**
	 * Access API via GET method.
	 *
	 * @param route API route.
	 * @returns {Promise.<*>}
	 */
	get = async route => {
		try {
			this.lazyLoadAuthInfo();

			const authToken = reactLocalStorage.get("authToken", false);
			const response = await fetch(this.endpoint + route, {
				method: "get",
				mode: "cors",
				headers: {
					"X-App-Session-Id": this.lastAuthResponse.sessionId || "",
					"X-App-Token": authToken
				}
			});

			if (response.ok) {
				this.extractNewSessionId(response);
				this.extractNewAuthToken(response);
				let jsonResponse = await response.json();
				return jsonResponse;
			} else {
				reactLocalStorage.clear();
				window.location.reload();
				return false;
			}
		} catch ($exception) {

			return false;
		}
	};

	/**
	 * Lazy load auth info into client.
	 *
	 * @return void
	 */
	lazyLoadAuthInfo = () => {
		if ( this.lastAuthResponse && !this.lastAuthResponse.hasOwnProperty("sessionId")) {
			let authInfo = reactLocalStorage.getObject("authInfo");
			this.lastAuthResponse = authInfo;
		}
	};

	/**
	 * Extract new X-App-Session-Id from response headers and store in in the bucket if needed.
	 *
	 * @param response
	 * @return {Promise.<void>}
	 */
	extractNewSessionId = async response => {
		if (response.headers.has("x-app-session-id")) {
			let appSessionId = response.headers.get("x-app-session-id");
			if (Array.isArray(appSessionId) && appSessionId.length > 0) {
				appSessionId = appSessionId[0];
			}
			if (this.lastAuthResponse.sessionId !== appSessionId) {
				this.lastAuthResponse.sessionId = appSessionId;
				reactLocalStorage.setObject("authInfo", this.lastAuthResponse);
			}
		}
	};

	/**
	 * Extract new X-App-Token from response headers and store in in the bucket if needed.
	 *
	 * @param response
	 * @return {Promise.<void>}
	 */
	extractNewAuthToken = async response => {
		if (response.headers.has("X-App-Token")) {
			let authToken = response.headers.get("X-App-Token");
			if (authToken) {
				reactLocalStorage.set("authToken", authToken);
			}
		}
	};

	/**
	 * Check if Middleware has sent Compete reauth request and log user out if needed.
	 *
	 * @param response
	 */
	checkReauthCommand = response => {
		if (response.headers.has("X-App-Reauth") || response.headers.has("x-app-reauth")) {
			this.lastAuthResponse = {};
			reactLocalStorage.clear();
			window.location.reload();
			return true;
		}
		return false;
	};

	/**
	 * Get club locations from middleware.
	 *
	 * @return {Promise}
	 */
	pullClubs = () => {
		return new Promise(async (resolve, reject) => {
			const response = await this.get("/clubs");
			if (response.hasOwnProperty("error") && response.error) {
				resolve(false);
			} else {
				resolve(response.clubs);
			}
		});
	};

	/**
	 * Get resource locations from middleware.
	 *
	 * @return {Promise}
	 */
	pullResources = (clubId) => {
		return new Promise(async (resolve, reject) => {
			const response = await this.get(`/resources?club=${clubId}`);
			if (response.hasOwnProperty("error") && response.error) {
				resolve(false);
			} else {
				resolve(response.resources);
			}
		});
	};
};

export default new MiddlewareClient({});



// WEBPACK FOOTER //
// ./src/actions/middleware.js